import {
  BRAND_PAGE_SLUGS,
  CATEGORY_PAGE_SLUGS,
  normalizeCategorySlug,
  SPARE_PARTS_SUBCATEGORY_SLUGS,
} from '@/constants/category-pages';

export interface PdpCategory {
  id?: string;
  categoryId?: string;
  name?: string;
  path?: string;
  slug?: string;
  parentKey?: string;
  parentName?: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuid = (value: string) => UUID_PATTERN.test(value);

const isLocaleSegment = (segment: string, localeString: string) =>
  segment === localeString || segment === 'au' || segment === 'nz' || segment.startsWith('en-');

const getPathSegments = (path: string, localeString: string) =>
  path.split('/').filter(Boolean).filter((segment) => !isLocaleSegment(segment, localeString));

const normalizedCategoryPageSlugs = CATEGORY_PAGE_SLUGS.map(normalizeCategorySlug);

const isTopLevelCategoryPage = (slug: string) => {
  const normalized = normalizeCategorySlug(slug);
  return (
    normalizedCategoryPageSlugs.includes(normalized) ||
    BRAND_PAGE_SLUGS.includes(normalized) ||
    SPARE_PARTS_SUBCATEGORY_SLUGS.includes(normalized)
  );
};

const findCategoryByKey = (categories: PdpCategory[], key: string) =>
  categories.find(
    (category) =>
      category.slug === key ||
      category.categoryId === key ||
      category.id === key,
  );

const resolveSegmentSlug = (segment: string, categories: PdpCategory[]): string => {
  if (!isUuid(segment)) return segment;
  return findCategoryByKey(categories, segment)?.slug ?? segment;
};

const buildCategoryChain = (categories: PdpCategory[], localeString: string): PdpCategory[] => {
  const deepestCategory = categories.reduce((deepest, current) =>
    getPathSegments(current.path || '', localeString).length >
    getPathSegments(deepest.path || '', localeString).length
      ? current
      : deepest,
  categories[0]);

  const chain: PdpCategory[] = [deepestCategory];
  let parentKey = deepestCategory.parentKey;

  while (parentKey) {
    const child = chain[0];
    const parent = findCategoryByKey(categories, parentKey);

    if (parent) {
      chain.unshift(parent);
      parentKey = parent.parentKey;
      continue;
    }

    if (!isUuid(parentKey)) {
      chain.unshift({
        slug: parentKey,
        name: child.parentName || parentKey.replace(/-/g, ' '),
      });
    }

    break;
  }

  if (chain.length > 1 || !deepestCategory.path) {
    return chain;
  }

  const pathSegments = getPathSegments(deepestCategory.path, localeString).map((segment) =>
    resolveSegmentSlug(segment, categories),
  );

  if (pathSegments.length <= 1) {
    return chain;
  }

  return pathSegments.map((slug, index) => {
    if (index === pathSegments.length - 1) {
      return deepestCategory;
    }

    const match = categories.find((category) => category.slug === slug);
    return (
      match ?? {
        slug,
        name: slug.replace(/-/g, ' '),
      }
    );
  });
};

const toSubCategoryQuerySlug = (category: PdpCategory) => {
  const name = category.name?.trim();
  if (name) {
    return normalizeCategorySlug(name);
  }

  return normalizeCategorySlug(category.slug || '').replace(/-l\d+$/, '');
};

const buildCategoryPageLink = (category: PdpCategory, index: number, chain: PdpCategory[]) => {
  const slug = category.slug || '';

  if (!slug || isUuid(slug)) {
    return '/';
  }

  if (isTopLevelCategoryPage(slug)) {
    return `/product/${slug}`;
  }

  const parentSlug = chain[index - 1]?.slug;
  if (parentSlug && !isUuid(parentSlug)) {
    return `/product/${parentSlug}?subCategory=${encodeURIComponent(toSubCategoryQuerySlug(category))}`;
  }

  return `/product/${slug}`;
};

export const buildPdpBreadcrumbs = (
  categories: PdpCategory[],
  localeString: string,
): Array<{ name: string; link: string }> => {
  if (!categories?.length) return [];

  const chain = buildCategoryChain(categories, localeString);
  if (!chain.length) return [];

  return chain.map((category, index) => {
    const slug = category.slug || '';
    const name = category.name || slug.replace(/-/g, ' ');

    return {
      name,
      link: buildCategoryPageLink(category, index, chain),
    };
  });
};
